import { MenuFourDotsIcon, TranslateIcon } from '@ghq-abi/design-system-icons';
import {
  Avatar,
  Button,
  DropdownMenu,
  MainNav
} from '@ghq-abi/design-system-v2';
import { useTranslate } from '@tolgee/react';
import { LanguagesEnum } from 'abi-opr-common-types';

import { ErrorBoundary } from '~/app/components';
import { useLanguageDropdown } from '~/app/components/LanguageDropdown/hooks';
import { useModuleItems } from '~/app/components/ModulesDropdown/hooks/useModuleItems';
import { useUserDropdown } from '~/app/components/UserDropdown/hooks';
import { useUser } from '~/shared/hooks/useUser';
import { normalizeLanguage } from '~/shared/utils/string';

type HubTemplateProps = {
  children: React.ReactNode;
};

const triggerStyle = { cursor: 'pointer' } as const;

function UserAvatar({ urlImage, name }: { urlImage?: string; name: string }) {
  const initials = (name || '??').substring(0, 2).toUpperCase();
  return (
    <Avatar.Root>
      <Avatar.Image src={urlImage} alt={name} />
      <Avatar.Fallback>{initials}</Avatar.Fallback>
    </Avatar.Root>
  );
}

function HubLanguageDropdown() {
  const { t } = useTranslate();
  const user = useUser();
  const { handleLanguageChange } = useLanguageDropdown();
  const currentLang = normalizeLanguage(user.language);

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant="tertiary" style={triggerStyle}>
          <TranslateIcon width={18} height={18} />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="w-56" align="end" sideOffset={8}>
        <DropdownMenu.RadioGroup
          value={currentLang}
          onValueChange={handleLanguageChange}>
          {Object.keys(LanguagesEnum).map(lang => (
            <DropdownMenu.RadioItem key={lang} value={lang}>
              {t(`languages.${lang}`)}
            </DropdownMenu.RadioItem>
          ))}
        </DropdownMenu.RadioGroup>
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

function HubModulesDropdown() {
  const { t } = useTranslate();
  const { items } = useModuleItems();

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <Button variant="tertiary" style={triggerStyle}>
          <MenuFourDotsIcon width={18} height={18} />
        </Button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="w-56" align="end" sideOffset={8}>
        <DropdownMenu.Label>
          {t('modules.quick_access', 'Quick Access')}
        </DropdownMenu.Label>
        {items.map(item => (
          <DropdownMenu.Item
            key={item.label}
            subtitle={item.subtitle}
            onSelect={() => {
              if (item.url) {
                window.open(item.url, '_blank');
              }
            }}>
            {item.label}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

function HubUserDropdown() {
  const user = useUser();
  const { items, profileInfo } = useUserDropdown({ user });

  return (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger asChild>
        <button
          style={{ cursor: 'pointer', border: 0, background: 'transparent' }}>
          <UserAvatar urlImage={profileInfo.urlImage} name={profileInfo.name} />
        </button>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content className="w-56" align="end" sideOffset={8}>
        <DropdownMenu.AvatarLabel
          avatar={
            <UserAvatar
              urlImage={profileInfo.urlImage}
              name={profileInfo.name}
            />
          }
          subtitle={profileInfo.zone}>
          {profileInfo.name}
        </DropdownMenu.AvatarLabel>
        <DropdownMenu.Separator />
        {items.map(item => (
          <DropdownMenu.Item
            key={item.label}
            icon={item.icon}
            onSelect={item.onSelect}>
            {item.label}
          </DropdownMenu.Item>
        ))}
      </DropdownMenu.Content>
    </DropdownMenu.Root>
  );
}

const HUB_LAYOUT_STYLES = `
  .hub-layout [data-sidebar="header"] {
    padding-left: 2rem !important;
    padding-top: 0 !important;
    padding-bottom: 0 !important;
    border-bottom-width: 1px !important;
    box-shadow: none !important;
  }
  .hub-layout [data-sidebar="trigger"],
  .hub-layout [data-sidebar="sidebar"],
  .hub-layout [data-sidebar="rail"] {
    display: none !important;
    width: 0 !important;
    min-width: 0 !important;
  }
  .hub-layout main,
  .hub-layout [data-sidebar="inset"] {
    margin: 0 !important;
    margin-left: 0 !important;
    padding: 0 !important;
    padding-left: 0 !important;
    border-radius: 0 !important;
    box-shadow: none !important;
    width: 100% !important;
    max-width: 100% !important;
  }
  .hub-layout main > div,
  .hub-layout [data-sidebar="inset"] > div {
    padding: 0 !important;
  }
`;

export function HubTemplate({ children }: HubTemplateProps) {
  const topbarOptions = [
    <HubLanguageDropdown key="lang" />,
    <HubModulesDropdown key="modules" />,
    <HubUserDropdown key="user" />
  ];

  return (
    <div className="hub-layout">
      <style>{HUB_LAYOUT_STYLES}</style>
      <MainNav
        topbar={{
          textLogo: 'Operations Hub',
          options: topbarOptions
        }}>
        <main className="min-h-screen bg-background">
          <ErrorBoundary inApp>{children}</ErrorBoundary>
        </main>
      </MainNav>
    </div>
  );
}
